import React from 'react';
import propTypes from 'prop-types';
import { Form } from 'shared-ui';
import { isBoolean, isDefined, isValidDate, ObjectAccessor, formatDate } from '@dateam/ark';
import { concatClassName } from '@dateam/ark-react';
import { plotDetailsPropTypes } from 'data/plot';
import { validate as validator, ValidationError } from 'data/observation';
import { MODE_VALUES } from 'data/observation/observation28';
import { ObservationProps } from './types';
import { ObservationErrors } from './components';
import ObservationField from './ObservationField';
import ObservationItem from './ObservationItem';
import styles from './Observation.module.scss';

const observationKey = 'observation28';

type Props = ObservationProps<App.Observation28>;

const getConfusionLabel = (confusion: boolean | null) => {
    if (isBoolean(confusion)) return confusion ? 'Oui' : 'Non';

    return '-';
};

const Observation28: React.FC<Props> = ({
    data,
    plot,
    ignorable,
    className,
    onChange
}: Props) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, ValidationError>>({});
    const internalData = React.useRef(data);

    React.useEffect(() => {
        internalData.current = data;
    }, [data]);

    React.useEffect(() => {
        if (data.presence === undefined &&
            data.comment === undefined) return;

        setValidationErrors(validator(data, plot));
    }, [plot, data]);

    const presence = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.presence ?? undefined;
    }, [data]);

    const mode = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.mode ?? undefined;
    }, [data]);

    const comment = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.comment ?? undefined;
    }, [data]);

    const previousValue = React.useMemo(() => {
        if (isDefined(internalData.current.previous)) {
            return (
                <>
                    {isValidDate(internalData.current.previous.updatedOn) && (
                        <div className={styles['previousDate']}>{formatDate(internalData.current.previous.updatedOn)}</div>
                    )}
                    <div className={styles['previous']}>Oui/Non: {getConfusionLabel(internalData.current.previous.presence?.value ?? null)}</div>
                    <div className={styles['previous']}>Mode: {internalData.current.previous.mode?.value ?? '-'}</div>
                </>
            );
        }

        return (<div>Aucune valeur disponible</div>);
    }, [internalData.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIgnoreObservation = React.useCallback(() => {
        const newData: App.Observation28 = {
            ...internalData.current,
            presence: null,
            comment: null,
            ignored: !internalData.current.ignored
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handlePresenceChange = React.useCallback((newValue: boolean | undefined) => {
        const newData: App.Observation28 = {
            ...internalData.current,
            presence: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleModeChange = React.useCallback((newValue: string | undefined) => {
        const newData: App.Observation28 = {
            ...internalData.current,
            mode: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleCommentChange = React.useCallback((newValue: string | undefined) => {
        const newData: App.Observation28 = {
            ...internalData.current,
            comment: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleFocus = React.useCallback((field: ObjectAccessor<Omit<App.Observation28, 'previous'>>) => {
        if (internalData.current[field] !== undefined) return;

        const newData: App.Observation28 = {
            ...internalData.current,
            [field]: null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    return (
        <ObservationItem
            data={data}
            ignorable={ignorable}
            onIgnore={handleIgnoreObservation}
            previous={previousValue}
            label="Tordeuses - Confusion"
        >
            <div className="row">
                <ObservationField
                    id={`${observationKey}-presence`}
                    label="Oui/Non"
                    className={concatClassName(styles[`${observationKey}-presence`], 'col-4')}
                >
                    <Form.Select
                        selected={presence}
                        onChange={handlePresenceChange}
                        state={validationErrors['presence']?.type}
                        onFocus={() => handleFocus('presence')}
                        required={!data.ignored}
                        readOnly={data.ignored}
                    >
                        <Form.Select.Option value={null} />
                        <Form.Select.Option value={true}>Oui</Form.Select.Option>
                        <Form.Select.Option value={false}>Non</Form.Select.Option>
                    </Form.Select>
                </ObservationField>
                <ObservationField
                    id={`${observationKey}-mode`}
                    label="Mode de confusion"
                    className={concatClassName(styles[`${observationKey}-mode`], 'col-4')}
                >
                    <Form.Select
                        selected={mode}
                        onChange={handleModeChange}
                        state={validationErrors['mode']?.type}
                        onFocus={() => handleFocus('mode')}
                        required={!data.ignored && presence === true}
                        readOnly={data.ignored}
                    >
                        <Form.Select.Option value={null} />
                        {MODE_VALUES.map(option => (
                            <Form.Select.Option key={option} value={option}>
                                {option}
                            </Form.Select.Option>
                        ))}
                    </Form.Select>
                </ObservationField>
            </div>
            <div className="row">
                <ObservationField
                    id={`${observationKey}-comment`}
                    label="Remarque"
                    className={concatClassName(styles[`${observationKey}-comment`], 'col-12')}
                >
                    <Form.Input
                        multiline
                        rows={2}
                        value={comment}
                        onChange={handleCommentChange}
                        state={validationErrors['comment']?.type}
                        readOnly={data.ignored}
                    />
                </ObservationField>
            </div>
            <ObservationErrors value={validationErrors} observationKey={observationKey} />
        </ObservationItem>
    );
};

Observation28.propTypes = {
    data: propTypes.any.isRequired,
    plot: plotDetailsPropTypes.isRequired,
    className: propTypes.string,
    onChange: propTypes.func
};

Observation28.defaultProps = {
    className: undefined,
    onChange: undefined
};

export default Observation28;
